// apps/client/src/pages/admin/dashboard/client/components/ManualCreateUserDialog.tsx

import { useDialog } from "@/client/stores/dialog";
import { ManualCreateUserDto } from "@reactive-resume/dto";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormField,
  FormLabel,
  Input,
  FormItem,
  FormControl,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
  Checkbox,
} from "@reactive-resume/ui";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useRegisterByAdmin } from "@/client/services/user/create-user";
import { useUpdateUserByAdmin } from "@/client/services/user/update-user";
import { useRoles, useAssignRoleToUser, useRemoveRoleFromUser } from "@/client/services/role/role";
import { toast } from "sonner";

export type UserFormPayload = {
  userId?: string;
  isInternal?: boolean;
  user?: {
    name?: string;
    email?: string;
    isInternal?: boolean;
  };
  currentRoles?: any[];
};

type FormValue = {
  name: string;
  email: string;
  isInternal: boolean;
  roleId?: string;
};

export const ManualCreateUserDialog = () => {
  const formRef = useRef<HTMLFormElement>(null);
  const { registerByAdmin } = useRegisterByAdmin();
  const { mutateAsync: updateUser, isPending: updating } = useUpdateUserByAdmin();
  const { mutateAsync: assignRole } = useAssignRoleToUser();
  const { mutateAsync: removeRole } = useRemoveRoleFromUser();
  const { data: roles } = useRoles();

  const { isOpen, mode, close, payload } = useDialog<UserFormPayload>("create-user");

  const isEdit = mode === "update";
  const isInternal = payload?.item?.isInternal ?? payload?.item?.user?.isInternal ?? false;
  const userId = payload?.item?.userId;
  const currentRoles = payload?.item?.currentRoles ?? [];

  const form = useForm<FormValue>({
    defaultValues: {
      name: payload?.item?.user?.name ?? "",
      email: payload?.item?.user?.email ?? "",
      isInternal,
      roleId: currentRoles[0]?.id ?? "",
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    form.reset({
      name: payload?.item?.user?.name ?? "",
      email: payload?.item?.user?.email ?? "",
      isInternal,
      roleId: currentRoles[0]?.id ?? "",
    });
  }, [isOpen, payload]);

  const OnSubmit = async (data: FormValue) => {
    try {
      if (isEdit && userId) {
        // ----- MISE À JOUR -----
        await updateUser({
          userId,
          data: {
            name: data.name,
            email: data.email,
          },
        });

        if (isInternal && data.roleId) {
          const previousRoleId = currentRoles[0]?.id;
          if (previousRoleId && previousRoleId !== data.roleId) {
            await removeRole({ userId, roleId: previousRoleId });
          }
          if (previousRoleId !== data.roleId) {
            await assignRole({ userId, roleId: data.roleId });
          }
        }

        toast.success("Utilisateur mis à jour avec succès");
      } else {
        // ----- CRÉATION -----
        // Le mot de passe est généré et envoyé par e-mail côté serveur :
        // il n'est jamais affiché ni transmis par le front.
        const payloadCreate: ManualCreateUserDto = {
          name: data.name,
          email: data.email,
          isInternal: data.isInternal,
          ...(data.isInternal && data.roleId ? { roleId: data.roleId } : {}),
        };
        await registerByAdmin(payloadCreate);
        toast.success(
          `Compte créé avec succès — un e-mail avec les identifiants a été envoyé à ${data.email}`,
        );
      }
      form.reset();
      close();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Une erreur est survenue");
      form.reset();
    }
  };

  const dialogTitle = isEdit
    ? isInternal
      ? "Modifier l'administrateur"
      : "Modifier le client"
    : isInternal
      ? "Ajouter un administrateur"
      : "Nouveau client";

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader className="border-b border-admin-border pb-4">
          <DialogTitle className="text-xl text-admin-text-primary">{dialogTitle}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            ref={formRef}
            onSubmit={form.handleSubmit(OnSubmit)}
            className="flex flex-col gap-y-5 py-4"
          >
            {/* Nom */}
            <FormField
              name="name"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-bold uppercase tracking-wider text-admin-text-muted">
                    Nom complet
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="John Doe" className="bg-admin-background" />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Email */}
            <FormField
              name="email"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-bold uppercase tracking-wider text-admin-text-muted">
                    Adresse email
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      className="bg-admin-background lowercase"
                      placeholder="john.doe@example.com"
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* isInternal – uniquement en création */}
            {!isEdit && (
              <div className="flex items-center gap-3 rounded-lg border border-admin-border bg-admin-background p-3">
                <Checkbox
                  id="isInternal"
                  checked={form.watch("isInternal")}
                  onCheckedChange={(checked) => form.setValue("isInternal", Boolean(checked))}
                />
                <div className="flex flex-col">
                  <Label
                    htmlFor="isInternal"
                    className="cursor-pointer text-sm font-medium text-admin-text-primary"
                  >
                    Utilisateur interne
                  </Label>
                  <span className="text-xs text-admin-text-muted">
                    Cochez pour créer un administrateur
                  </span>
                </div>
              </div>
            )}

            {/* Rôle – dès que l'utilisateur est interne (création et modification) */}
            {form.watch("isInternal") && (
              <FormField
                name="roleId"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-bold uppercase tracking-wider text-admin-text-muted">
                      Rôle
                    </FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={(value) => form.setValue("roleId", value)}
                    >
                      <FormControl>
                        <SelectTrigger className="bg-admin-background">
                          <SelectValue placeholder="Choisir un rôle" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {roles?.map((role) => (
                          <SelectItem key={role.id} value={role.id}>
                            <div className="flex items-center gap-2">
                              <span>{role.name}</span>
                              {role.isSystem && (
                                <Badge className="border-0 bg-admin-surface-hover text-[10px] text-admin-text-secondary">
                                  système
                                </Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            )}
          </form>
        </Form>

        <DialogFooter className="gap-2 border-t border-admin-border pt-4">
          <Button
            type="button"
            variant="ghost"
            onClick={close}
            className="text-admin-text-muted hover:text-admin-text-primary"
          >
            Annuler
          </Button>
          <Button
            type="submit"
            onClick={form.handleSubmit(OnSubmit)}
            disabled={updating}
            className="bg-admin-primary text-admin-primary-foreground hover:bg-admin-primary/90"
          >
            {isEdit ? "Enregistrer" : "Créer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
